import { useMemo, useState } from "react";

import {
    Column,
    InvisibleSelect,
    Row,
    SelectOption,
    StatColumn,
    StatProps,
    Text,
    ToggleChevron
} from "@bridgesplit/ui";
import { combineCollections, filterTruthy, formatPercent, formatUsd, TIME } from "@bridgesplit/utils";
import {
    getSimpleInterest,
    isLstLoop,
    isValidLeverage,
    LoopscalePointsAction,
    isRatexLoop,
    isExponentLoop,
    useLoopOraclePriceQuotes
} from "@bridgesplit/abf-react";
import { Collapse, Divider } from "@mui/material";
import { useLoopscalePointStat, useOracleDiscrepencyStats } from "app/components/points/stats";

import { useLoopContext } from "../LoopContext";
import { useWindContext } from "./WindContext";
import { WindBreakEvenStats } from "./WindBreakEvenStats";
import { LoopMode } from "./types";

const TIME_OPTIONS: SelectOption<number>[] = [
    { label: "per week", value: TIME.WEEK },
    { label: "per month", value: TIME.MONTH },
    { label: "per year", value: TIME.YEAR }
];
export default function WindNestedStats() {
    const { quote, form, isQuoteFetching, isQuoteLoading, oraclePrices, externalQuoteError, loopMode } =
        useWindContext();
    const { loopExpanded } = useLoopContext();
    const { principalOracle } = useLoopOraclePriceQuotes(loopExpanded);

    const [estimateSeconds, setEstimateSeconds] = useState(TIME_OPTIONS[TIME_OPTIONS.length - 1].value);

    const yieldInfo = useMemo(() => {
        if (!quote || !loopExpanded || !principalOracle) return undefined;
        const inputOracle = oraclePrices?.get(form?.inputToken?.mint || "");
        const estimatedCollateralValue =
            (inputOracle?.getUsdAmount(form.inputAmount ?? 0) ?? 0) * quote.leverageMultiplier;
        const estimatedPrincipalValue = principalOracle?.getUsdAmount(quote.principalAmount);

        const apy = quote?.netApyPct;
        const estimatedNetPositionValue = Math.max(estimatedCollateralValue - estimatedPrincipalValue, 0);

        const yieldUsd = getSimpleInterest({
            principal: estimatedNetPositionValue,
            apy,
            loanDuration: estimateSeconds,
            subtractPrincipal: true
        });
        return {
            apy,
            yieldUsd
        };
    }, [quote, loopExpanded, oraclePrices, form.inputAmount, estimateSeconds, form.inputToken, principalOracle]);

    const totalBorrow = (quote?.principalAmount ?? 1) * (principalOracle?.usdPrice ?? 1);

    const loopRewards = useLoopscalePointStat({
        action: LoopscalePointsAction.Borrow,
        totalPrincipalAmountUsd: totalBorrow,
        variant: "body1",
        textSelectColor: "secondary",
        identifier: loopExpanded?.principalToken.mint
    });

    // Only use principal token for oracle discrepancy stats as flash loan uses principal token
    const oracleDiscrepencyStats = useOracleDiscrepencyStats({
        metadata: [loopExpanded?.collateralToken].filter(filterTruthy),
        variant: "body2"
    });

    const showBreakevenStats =
        loopExpanded &&
        !externalQuoteError &&
        (isLstLoop(loopExpanded) || isRatexLoop(loopExpanded) || isExponentLoop(loopExpanded)) &&
        loopMode === LoopMode.Auto;

    const apy = yieldInfo?.apy;
    const yieldUsd = yieldInfo?.yieldUsd;
    const borrowApy = quote?.bestQuote?.apy;
    const collateralApy = loopExpanded?.loopVault.collateralApyPct;
    const collateralApyLabel = `${loopExpanded?.collateralToken.symbol ?? "Collateral"} APY`;

    const baseStats: StatProps[] = [
        {
            caption: (
                <Row spacing={0.5}>
                    <Text color="caption">Est. yield</Text>
                    <InvisibleSelect
                        color="secondary"
                        value={estimateSeconds}
                        setValue={setEstimateSeconds}
                        options={TIME_OPTIONS}
                    />
                </Row>
            ),
            value: <Text translate="no">{formatUsd(yieldUsd)}</Text>,
            hide: loopMode === LoopMode.Manual
        }
    ];

    const stats = combineCollections([baseStats, oracleDiscrepencyStats, [loopRewards]]);

    if (showBreakevenStats) {
        stats.push({
            caption: "Break-even",
            value: (
                <WindBreakEvenStats
                    loopExpanded={loopExpanded}
                    isPtLoop={!isLstLoop(loopExpanded) && (isRatexLoop(loopExpanded) || isExponentLoop(loopExpanded))}
                />
            )
        });
    }

    if (useHideNestedStats()) return null;
    return (
        <Column
            spacing={0.5}
            sx={{
                opacity: isQuoteFetching && !isQuoteLoading ? 0.5 : 1,
                transition: "opacity 0.2s ease"
            }}
        >
            {!!collateralApy && (
                <NetApyDropdown
                    apy={apy}
                    borrowApy={borrowApy}
                    collateralApy={collateralApy}
                    collateralApyLabel={collateralApyLabel}
                    loading={isQuoteLoading}
                />
            )}
            <StatColumn loading={isQuoteLoading} stats={stats} />
        </Column>
    );
}

function useHideNestedStats() {
    const { form, quote, loopMode } = useWindContext();

    if (loopMode === LoopMode.Manual) return !form.collateralAmount || !form.principalAmount || !quote;

    return !form.inputAmount || !isValidLeverage(form.multiplier) || !quote;
}

function NetApyDropdown({
    apy,
    borrowApy,
    collateralApy,
    collateralApyLabel,
    loading
}: {
    apy: number | undefined;
    borrowApy: number | undefined;
    collateralApy: number | undefined;
    collateralApyLabel: string;
    loading: boolean;
}) {
    const [isExpanded, setIsExpanded] = useState(false);

    const stats = useMemo(
        () =>
            [
                {
                    caption: "Borrow APY",
                    value: formatPercent(borrowApy)
                },
                {
                    caption: collateralApyLabel,
                    value: formatPercent(collateralApy),
                    hide: collateralApy === undefined
                }
            ].filter((s) => !s.hide),
        [borrowApy, collateralApy, collateralApyLabel]
    );

    return (
        <Column>
            <Row sx={{ cursor: "pointer" }} spaceBetween>
                <Text color="caption" loading={loading}>
                    Net APY
                </Text>
                <Row spacing={1}>
                    <Text loading={loading}>{formatPercent(apy)}</Text>
                    <ToggleChevron
                        onClick={() => setIsExpanded(!isExpanded)}
                        textVariant="body1"
                        sx={{ p: 0 }}
                        expanded={isExpanded}
                    />
                </Row>
            </Row>
            <Collapse in={isExpanded}>
                <Column sx={{ mt: 1 }} spacing={1}>
                    <Divider />
                    <StatColumn loading={loading} stats={stats} />
                </Column>
            </Collapse>
        </Column>
    );
}
